/// <amd-module name="Analytics/Shared/AadTokenGenerator/AadTokenGenerator" />
import * as localStorageHelper from "Analytics/Shared/Powerbi/Utils/LocalCacheManager";
import * as Constants from "Analytics/Shared/AadTokenGenerator/Constants";
import * as Resources from "Analytics/Shared/AadTokenGenerator/Resources";
import * as React from "react";
import { IVssPageService, IPageData } from "VSS/Platform/Page";
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Uri } from "VSS/Platform/Util/Url";

export interface IAadProps extends IVssComponentProperties {
    instance?: string;
    tenant?: string;
    clientId?: string;
    callback: (token: IAadTokenResponse) => void;
}

export interface IAadTokenResponse{
    token?: string;
    errorText?: string;
}

export class AadComponent extends VssComponent<IAadProps> {

    private _userId: string;
    // cache invalidate time in minutes.
    private static readonly c_cacheInvalidateTime = 20;
    private static readonly c_visualStudioDomain = "visualstudio.com";

    constructor(props: IAadProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();
        this._userId = pageData!.user.id;
    }

    public render(): JSX.Element {
        const tokenFromLocalStorage = localStorageHelper.getCachedState(this._getAccessTokenCacheKey(this._userId), AadComponent.c_cacheInvalidateTime);
        if (tokenFromLocalStorage) {
            this.props.callback({token: tokenFromLocalStorage});
            return <></>;
        }

        const instance: string = this.props.instance ? this.props.instance : Constants.instance;
        const tenant: string = this.props.tenant ? this.props.tenant : Constants.tenant;
        const clientId: string = this.props.clientId ? this.props.clientId : Constants.client_id;
        const srcUrl = new Uri(instance.concat(tenant).concat("/oauth2/authorize"));
        let origin: string = window.location.origin;
        const isVisualStudioDomain: boolean = origin.toLocaleLowerCase().indexOf(AadComponent.c_visualStudioDomain) > -1;

        if (isVisualStudioDomain) {
            origin = origin + "/default";
        }

        srcUrl.queryParameters.push({
            name: "response_type",
            value: "token"
        });
        srcUrl.queryParameters.push({
            name: "client_id",
            value: clientId
        });
        srcUrl.queryParameters.push({
            name: "redirect_uri",
            value: origin
        });
        srcUrl.queryParameters.push({
            name: "nonce",
            value: Math.random().toString()
        });
        srcUrl.queryParameters.push({
            name: "resource",
            value: Constants.resource
        });

        return (
            <iframe
                className="analytics-aad-token"
                src={srcUrl.absoluteUri}
                onLoad={iframeElement => {
                    try {
                        const contentWindow = iframeElement.currentTarget.contentWindow;
                        const hash = contentWindow ? contentWindow.location.hash : "";

                        const filteredHash = hash.replace(/^#/, "");
                        const parameters = filteredHash.split("&");
                        const access_parameter = parameters.filter(parameter => parameter.split("=")[0] === "access_token");

                        if (access_parameter && access_parameter.length > 0) {
                            const accessToken = access_parameter[0].split("=")[1];

                            localStorageHelper.setCachedState(this._getAccessTokenCacheKey(this._userId), accessToken);
                            this.props.callback({ token: accessToken});
                        }
                    } catch (e) {
                        this.props.callback({errorText: Resources.AuthenticationErrorText})
                    }
                }}
            />
        );
    }

    private _getAccessTokenCacheKey(userId: string): string{
        return userId.concat("-accessToken");
    }
}
